import React, { forwardRef } from "react";
import styles from "./OptionListsComponents.module.css";
import { Typography, TypographyProps } from "@mui/material";
import { CoreCheckbox, CoreChip, CoreChipProps, CoreFormLabel, CoreInfoIndicator, CoreNoResults } from "../../../core";
import DefaultListIcon from "../../images/configuration/default-list.svg?react";
import i18nPackage from "../../../i18n/i18n";

type BaseListContainerProps = {
	children: React.ReactNode;
	className?: string;
};

export const BaseListContainer = forwardRef<HTMLDivElement, BaseListContainerProps>(function BaseListContainer(
	{ children, className },
	ref,
) {
	return (
		<div ref={ref} className={`${styles.baseListContainer} ${className || ""}`}>
			{children}
		</div>
	);
});

type BaseListHeaderContainerProps = {
	children: React.ReactNode;
	className?: string;
};

export function BaseListHeaderContainer({ children, className }: BaseListHeaderContainerProps) {
	return <div className={`${styles.baseListHeaderContainer} ${className || ""}`}>{children}</div>;
}

type BaseListFooterContainerProps = {
	children: React.ReactNode;
	className?: string;
};

export function BaseListFooterContainer({ children, className }: BaseListFooterContainerProps) {
	return <div className={`${styles.baseListFooterContainer} ${className || ""}`}>{children}</div>;
}

type BaseListInfoAreaProps = {
	children?: React.ReactNode;
	icon?: React.ReactNode;
	title: React.ReactNode;
	titleVariant?: TypographyProps["variant"];
	description?: React.ReactNode;
	slotProps?: {
		badge?: {
			text: React.ReactNode;
			chipColor?: string;
			chipProps?: Omit<CoreChipProps, "chipColor" | "children">;
		};
		hint?: {
			text: React.ReactNode;
			size?: number;
		};
	};
};

export function BaseListInfoArea({
	children,
	icon,
	description,
	title,
	titleVariant,
	slotProps,
}: BaseListInfoAreaProps) {
	const badgeProps = slotProps?.badge;
	const hintProps = slotProps?.hint;
	return (
		<div className={styles.baseListInfoContainer}>
			<div className={styles.baseListTitleRow}>
				<div className={styles.baseListTitleBadgeRow}>
					<div className={styles.baseListTitleIconWrapper}>
						<div className={styles.optionIconWrapper}>{icon ?? <DefaultListIcon />}</div>
						{typeof title === "string" ? (
							<Typography variant={titleVariant ?? "h3"} className={styles.baseListTitle}>
								{title}
							</Typography>
						) : (
							title
						)}
						{hintProps && <CoreInfoIndicator hint={hintProps.text} size={hintProps.size} />}
					</div>
					{badgeProps && <TotalSelectedBadge {...badgeProps} />}
				</div>
				{children}
			</div>
			{typeof description === "string" ? (
				<Typography className={styles.baseListDescription}>{description}</Typography>
			) : (
				description
			)}
		</div>
	);
}

type TotalSelectedBadgeProps = {
	text: React.ReactNode;
	chipColor?: string;
} & Omit<CoreChipProps, "chipColor" | "children">;

// Purple badge showed at the top
export function TotalSelectedBadge({ text, chipColor, ...props }: TotalSelectedBadgeProps) {
	return (
		<CoreChip className={styles.totalSelectedChip} {...props} chipColor={chipColor ?? "#F2F1FF"}>
			{text}
		</CoreChip>
	);
}

type SelectedBadgeProps = {
	text: React.ReactNode;
	chipColor?: string;
	tooltip?: string;
} & Omit<CoreChipProps, "chipColor" | "children" | "tooltip">;

// Yellow badge showed in specific rows, has a tooltip of the selected items
export function SelectedBadge({ text, chipColor, tooltip, ...props }: SelectedBadgeProps) {
	return (
		<CoreChip className={styles.selectedChip} tooltip={tooltip} {...props} chipColor={chipColor ?? "#F8EFE0"}>
			{text}
		</CoreChip>
	);
}

export type SelectAllContainerProps = {
	onChange: (selected: boolean) => void;
	checked: boolean;
	disabled?: boolean;
	className?: string;
	children?: React.ReactNode;
	slotProps?: {
		badge?: TotalSelectedBadgeProps;
	};
};

export function SelectAllContainer({
	onChange,
	className,
	checked,
	disabled,
	children,
	slotProps,
}: SelectAllContainerProps) {
	const badgeProps = slotProps?.badge;
	return (
		<div className={`${styles.selectAllContainer} ${className || ""}`}>
			<div className={styles.selectAllWrapper}>
				<CoreFormLabel
					control={<CoreCheckbox checked={checked} onChange={(_, checked) => onChange(checked)} disabled={disabled} />}
				>
					{i18nPackage.t("editQuizPage.components.selectAll")}
				</CoreFormLabel>
				{badgeProps && <TotalSelectedBadge {...badgeProps} />}
			</div>
			{children}
		</div>
	);
}

export type CommonListProps = {
	focusOnMount?: boolean;
	onFocus?: () => void;
};

/**UI team wants all edit quiz no results to be the same */
export function EditQuizNoResults() {
	return (
		<CoreNoResults variant="no-results-no-plus" header={i18nPackage.t("editQuizPage.components.noResultsTitle")}>
			{i18nPackage.t("editQuizPage.components.noResultsDesc")}
		</CoreNoResults>
	);
}
